import {
  Events,
  getAppliedFilters,
  getRfidConfigFromFlow,
  getWorkboardSidePanelKey,
  IFlow,
  IProperty,
  localForageUtil,
  makeDynamicWorkboardPageStore,
  PageViewModeEnum,
  SIDE_PEEK_ID_QUERY_PARAM,
  SortModel,
  useEnvironment,
  useOpenWorkWithRfid,
  useSidePeekDrawer,
  useWorkVersionDrawer,
} from '@diana/core';
import { GridSortModel } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { forwardRef, Fragment, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WorkKanbanBoard } from './WorkKanbanBoard';
import { WorkTable } from './WorkTable';

export const Content = forwardRef<
  GridApiPremium,
  {
    flow: IFlow;
    forceEmptySelection: boolean;
    supportedProperties: IProperty[];
    onSortModelChange: (model: GridSortModel) => void;
    sortModel: SortModel;
    isUserSidePeekPreference?: boolean;
    dataGridStorageKey: string;
  }
>((props, forwardedRef) => {
  const {
    flow,
    forceEmptySelection,
    supportedProperties,
    onSortModelChange,
    sortModel,
    isUserSidePeekPreference,
    dataGridStorageKey,
  } = props;
  const flowId = flow.id;
  const [searchParams] = useSearchParams();
  const useWorkboardPageStore = makeDynamicWorkboardPageStore(flow.flowOriginId);
  const filtersValues = useWorkboardPageStore((store) => store.filtersValues);
  const availableFilters = useWorkboardPageStore((store) => store.availableFilters);
  const appliedFilters = getAppliedFilters({ filtersValues });
  const viewMode = useWorkboardPageStore((store) => store.viewMode);
  const { openWorkDrawer } = useSidePeekDrawer();

  const showSidePeek =
    isUserSidePeekPreference ?? flow.featureFlags?.isSingleScreenWorkUpdate ?? true;

  useEffect(() => {
    localForageUtil
      .getItem<string>({ key: getWorkboardSidePanelKey(flow.flowOriginId) })
      .then((openedSidePanelId) => {
        const workId = (searchParams.get(SIDE_PEEK_ID_QUERY_PARAM) || openedSidePanelId) ?? null;

        if (workId && showSidePeek) {
          openWorkDrawer({ workId, flowId });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useWorkVersionDrawer();

  const environment = useEnvironment();

  useOpenWorkWithRfid({
    ...getRfidConfigFromFlow(flow),
    flow,
    onOpenWork: (work) => {
      if (showSidePeek) {
        openWorkDrawer({
          workId: work.id,
          flowId,
          displayName: work.displayName || work.workCode || '',
        });
      } else {
        Events.OpenWork.post({
          environment,
          workId: work.id,
          openInNewTab: false,
        });
      }
    },
  });

  return (
    <Fragment>
      {viewMode === PageViewModeEnum.Board && (
        <WorkKanbanBoard
          flow={flow}
          steps={flow.steps}
          showSidePeek={showSidePeek}
          availableFilters={availableFilters}
          filtersValues={filtersValues}
        />
      )}
      {(viewMode === PageViewModeEnum.Table || viewMode === PageViewModeEnum.TableExpanded) && (
        <WorkTable
          flow={flow}
          viewMode={viewMode}
          showSidePeek={showSidePeek}
          storageKey={dataGridStorageKey}
          forceEmptySelection={forceEmptySelection}
          ref={forwardedRef}
          supportedProperties={supportedProperties}
          appliedFilters={appliedFilters}
          availableFilters={availableFilters}
          filtersValues={filtersValues}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
        />
      )}
    </Fragment>
  );
});
